import React from "react";
import { Button, Card, Col, Descriptions, Flex, Row, Typography } from "antd";
import { useParams } from "react-router-dom";
import TimeAgo from "react-timeago";
import polishStrings from "react-timeago/lib/language-strings/pl";
import buildFormatter from "react-timeago/lib/formatters/buildFormatter";

import MainLayout from "components/MainLayout";

import useGet from "hooks/useGet";
import PageFailure from "components/PageFailure";
import dayjs from "dayjs";
import formatPrice from "helpers/formatPrice";
import { getInvoiceStatusTag } from "helpers/options/invoice_statuses";
import IssuesList from "./components/IssuesList";

const formatter = buildFormatter(polishStrings);

const ShowBillingSummary = () => {
  const { id } = useParams();

  const path = `/billing_summaries/${id}`;

  const {
    is_loading,
    is_error,
    data = {},
  } = useGet({
    path,
    query: ["billing_summary", id],
  });

  if (is_error) {
    return (
      <PageFailure
        title="Nie znaleziono rozliczenia"
        button_text="Wróć do listy"
        path="/billings"
      />
    );
  }

  return (
    <MainLayout
      back_href="/billings"
      breadcrumbs={[
        { name: "Moje faktuy", path: "/billings" },
        { name: data?.id || "Trwa pobieranie..." },
      ]}
    >
      <Row gutter={24} className="billing-container">
        <Col xl={12} lg={24} sm={24} xs={24}>
          <IssuesList billing_summary_id={id} />
        </Col>
        <Col xl={12} lg={24} sm={24} xs={24} className="billing-summary">
          <Card style={{ marginBottom: 24 }} loading={is_loading}>
            <Descriptions
              bordered
              size="small"
              items={[
                {
                  label: "ID",
                  children: data?.id,
                  span: 3,
                },
                {
                  label: "Okres rozliczenia",
                  children: data?.period_start
                    ? `${dayjs(data.period_start).format("DD")} - ${dayjs(
                        data.period_end
                      ).format("DD MMMM YYYY")}`
                    : "-",
                  span: 3,
                },
                {
                  label: "Ilość zgłoszeń",
                  children: data?.billed_issues_count,
                  span: 3,
                },
                {
                  label: "Cena za zgłoszenie",
                  children: data?.single_issue_fee
                    ? formatPrice(data.single_issue_fee, data.currency)
                    : "-",
                  span: 3,
                },
                {
                  label: "Koszt zgłoszeń",
                  children: data?.usage_fee
                    ? formatPrice(data.usage_fee, data.currency)
                    : "-",
                  span: 3,
                },
                {
                  label: "Abonament",
                  children: data?.base_fee
                    ? formatPrice(data.base_fee, data.currency)
                    : "-",
                  span: 3,
                },
                {
                  label: <strong>Kwota całkowita</strong>,
                  children: data?.total_amount ? (
                    <strong>
                      {formatPrice(data.total_amount, data.currency)}
                    </strong>
                  ) : (
                    "-"
                  ),
                  span: 3,
                },
                {
                  label: "Nr FV",
                  children: data?.invoice?.invoice_number || "-",
                  span: 3,
                },
                {
                  label: "Status",
                  children: getInvoiceStatusTag(data?.invoice?.status),
                  span: 3,
                },
                {
                  label: "Termin płatności",
                  children: data?.invoice?.due_date ? (
                    <Typography.Text
                      type={data?.invoice?.is_overdue ? "danger" : ""}
                    >
                      {dayjs(data.invoice.due_date).format("DD-MM-YYYY HH:mm")}
                      {" - "}
                      <strong>
                        <TimeAgo
                          date={data.invoice.due_date}
                          formatter={formatter}
                        />
                      </strong>
                    </Typography.Text>
                  ) : (
                    "-"
                  ),
                  span: 3,
                },
                {
                  label: "Data płatności",
                  children: data?.invoice?.paid_at ? (
                    <Typography.Text>
                      {dayjs(data.invoice.paid_at).format("DD-MM-YYYY HH:mm")}
                    </Typography.Text>
                  ) : (
                    "-"
                  ),
                  span: 3,
                },
              ]}
            />
            <Flex gap={12} justify="end" style={{ marginTop: 24 }}>
              <Button type="primary">Opłać</Button>
              <Button>Pobierz FV</Button>
            </Flex>
          </Card>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default ShowBillingSummary;
